import { faSearch } from "@fortawesome/pro-solid-svg-icons/faSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import * as React from "react";

// // // //

interface Props {
    value: string;
    name?: string;
    onChange: (updatedValue: string) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    rounded?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "full";
    color?: "gray" | "blue" | "green" | "orange" | "red";
}

/**
 * Input component for Search
 *
 * ENHANCEMENT - update this to be consistent with other form components
 *
 * ENHANCEMENT - add props.colorVariant using *new* ColorVariants enum
 */
export function SearchInput(props: Props) {
    const {
        value,
        name = "search",
        onChange,
        disabled,
        placeholder = "Search...",
        size = "md",
        rounded = "lg",
        color = "gray",
        className,
    } = props;

    return (
        <div
            className={cn("relative shadow-xs", className, {
                "rounded-none": rounded === "none",
                "rounded-xs": rounded === "xs",
                rounded: rounded === "sm",
                "rounded-md": rounded === "md",
                "rounded-lg": rounded === "lg",
                "rounded-xl": rounded === "xl",
                "rounded-full": rounded === "full",
            })}
        >
            <div
                className={cn(
                    "pointer-events-none absolute inset-y-0 flex items-center pl-3",
                    {
                        "left-0": size === "xs" || size === "sm",
                        "left-0.5": size === "md",
                        // TODO - finish implementing this
                        // "left-0.5": size === InputSizes.lg,
                        // "left-0.5": size === InputSizes.xl,
                    },
                )}
            >
                <FontAwesomeIcon
                    icon={faSearch}
                    className={cn({
                        // Size
                        "h-2.5 w-2.5": size === "xs",
                        "h-3 w-3": size === "sm",
                        "h-4 w-4": size === "md",
                        "h-6 w-6": size === "lg",
                        "h-7 w-7": size === "xl",
                        // Color
                        "text-slate-500": color === "gray",
                        "text-blue-500": color === "blue",
                        "text-emerald-500": color === "green",
                        "text-amber-500": color === "orange",
                        "text-red-500": color === "red",
                    })}
                    aria-hidden="true"
                />
            </div>
            <input
                name={name}
                type="search"
                className={cn(
                    "block bg-card w-full border placeholder:font-normal placeholder:tracking-wide",
                    {
                        // Size
                        "pl-6 py-0.5 placeholder:text-xs text-xs":
                            size === "xs",
                        "pl-8 py-1 text-sm": size === "sm",
                        "pl-10 py-2 text-normal": size === "md",
                        "pl-11 py-3 text-lg": size === "lg",
                        "pl-12 py-4 text-xl": size === "xl",
                        // Rounded
                        "rounded-none": rounded === "none",
                        "rounded-xs": rounded === "xs",
                        rounded: rounded === "sm",
                        "rounded-md": rounded === "md",
                        "rounded-lg": rounded === "lg",
                        "rounded-xl": rounded === "xl",
                        "rounded-full": rounded === "full",
                        "cursor-not-allowed": disabled,
                        // Color
                        "border-border focus:border-blue-500 focus:ring-blue-500 placeholder:text-slate-600":
                            color === "gray",
                        "border-blue-400 focus:border-blue-500 focus:ring-blue-500 placeholder:text-blue-600":
                            color === "blue",
                        "border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500 placeholder:text-emerald-600":
                            color === "green",
                        "border-amber-400 focus:border-amber-500 focus:ring-amber-500 placeholder:text-amber-600":
                            color === "orange",
                        "border-red-400 focus:border-red-500 focus:ring-red-500 placeholder:text-red-600":
                            color === "red",
                    },
                )}
                value={value}
                disabled={disabled}
                placeholder={placeholder}
                onKeyDown={props.onKeyDown}
                onChange={(e) => {
                    if (!disabled) {
                        const updatedValue = e.currentTarget.value;
                        onChange(updatedValue);
                    }
                }}
            />
        </div>
    );
}
